import { DecisionGraph, type DecisionGraphInput, EDGE_FROM_RELEASE, EDGE_FROM_STATE } from "./DecisionGraph";
import { type DecisionGraphRelease, documentRef } from "./DecisionGraphRelease";
import { rehydrateGraph } from "./GraphRehydrate";

/**
 * Builds the legacy `{type: "graph"}` result from a decision graph, exactly as the engine used to serialise
 * it (services/decisionapi/rust/src/result_payload_graph.rs), so callers that still ask for a `graph` keep
 * getting the same shape while the engine only produces decision graphs.
 */

/** The `{type: "graph"}` response element flags the engine understood. */
export type LegacyGraphElement = {
  id?: string;
  debug?: boolean;
  minimal?: boolean;
  debugInstancesUnhydrated?: boolean;
  pathingUnhydrated?: boolean;
  compressEdges?: boolean;
  noEdges?: boolean;
  compressed?: boolean;
  documentsAsGuids?: boolean;
  [key: string]: unknown;
};

const ELEMENT_KEYS = [
  "id",
  "debug",
  "minimal",
  "debugInstancesUnhydrated",
  "pathingUnhydrated",
  "compressEdges",
  "noEdges",
  "compressed",
  "documentsAsGuids",
] as const;

/**
 * @param input the decision graph (bytes, base64, payload or DecisionGraph)
 * @param element the `graph` element as the engine would have received it
 * @param release needed for debug, non-minimal elements, which carry each node's rule data
 */
export const decisionGraphToLegacyGraph = (
  input: DecisionGraphInput,
  element: LegacyGraphElement,
  release?: DecisionGraphRelease,
) => {
  // (read from the container's tables: an edited graph is written out first)
  const graph = DecisionGraph.from(input).toPacked();
  const debug = element.debug === true;
  const minimal = element.minimal === true;
  const pathingUnhydrated = element.pathingUnhydrated === true;
  const noEdges = element.noEdges === true;
  const compressEdges = element.compressEdges === true;
  const debugInstancesUnhydrated = element.debugInstancesUnhydrated === true;
  const omitPathing = minimal || pathingUnhydrated;
  const wantsRuleData = debug && !minimal;

  if (debug && !graph.debug) {
    throw new Error("A debug graph needs a decision graph requested with debug: true");
  }
  if (wantsRuleData && !release) {
    throw new Error("A debug, non-minimal graph needs the release to add each node's rule data");
  }
  const documents = wantsRuleData ? release?.documents : undefined;

  const nodes: { v: string; value: Record<string, unknown> }[] = new Array(graph.nodeCount());
  for (const id of graph.ids()) {
    const global = graph.isGlobal(id);
    // result_payload_graph.rs: instance copies skip rule data when debugInstancesUnhydrated
    const fullNode = wantsRuleData && !(debugInstancesUnhydrated && !global);
    const ruleNode = wantsRuleData ? release!.ruleNode(graph.guid(id)) : undefined;
    const values = graph.values(id);
    const value: Record<string, unknown> = { ...(fullNode && ruleNode ? ruleNode : {}), ...graph.properties(id) };

    if (values?.input !== undefined) value.input = values.input;
    if (values?.derived !== undefined) value.derived = values.derived;
    if (fullNode) value.hidden = graph.isHidden(id);
    if (!omitPathing) {
      const index = graph.index(id);
      if (index !== undefined) value.index = index;
    }
    if (!omitPathing && !global) value.path = graph.deref(id);
    if (!omitPathing && graph.session) {
      const parentPath = graph.parentPath(id);
      if (parentPath !== undefined) value.parent_path = parentPath;
    }
    if (values?.justification !== undefined) value.justification = values.justification;
    if (values?.triggered !== undefined) value.triggered = values.triggered;
    if (documents && ruleNode) {
      const asGuids = element.documentsAsGuids === true;
      if (typeof ruleNode.definedIn === "string") {
        const definedIn = documentRef(documents, ruleNode.definedIn, asGuids);
        if (definedIn !== undefined) value.definedIn = definedIn;
      }
      if (Array.isArray(ruleNode.usedIn)) {
        value.usedIn = ruleNode.usedIn
          .map((item: string) => documentRef(documents, item, asGuids))
          .filter((item: string | undefined) => item !== undefined);
      }
    }
    if (debug && values?.reason) value.debugInfo = { reason: values.reason };

    nodes[id] = { v: graph.deref(id), value };
  }

  const edges: { v: string; w: string; value?: unknown }[] = [];
  const instancePaths: string[] = [];
  if (!noEdges) {
    if (!compressEdges && !(minimal && !debug)) {
      for (const [from, to, sources] of graph.edgeEntries()) {
        if (sources & EDGE_FROM_RELEASE) edges.push({ v: graph.guid(from), w: graph.guid(to) });
      }
    }
    if (compressEdges) {
      // global -> global stays explicit; everything else aggregates per guid pair with instance indexes
      const internedInstances = new Map<number, number>();
      const intern = (id: number): number | "global" => {
        const instance = graph.instance(id);
        if (instance < 0) return "global";
        let index = internedInstances.get(instance);
        if (index === undefined) {
          index = instancePaths.length;
          instancePaths.push(graph.instancePath(id)!);
          internedInstances.set(instance, index);
        }
        return index;
      };
      const aggregates = new Map<string, { v: string; w: string; instances: Map<string, Set<number | "global">> }>();
      for (const [from, to, sources] of graph.edgeEntries()) {
        if (!(sources & EDGE_FROM_STATE)) continue;
        if (graph.isGlobal(from) && graph.isGlobal(to)) {
          edges.push({ v: graph.deref(from), w: graph.deref(to) });
          continue;
        }
        const key = `${graph.template(from)}>${graph.template(to)}`;
        let aggregate = aggregates.get(key);
        if (!aggregate) {
          aggregate = { v: graph.guid(from), w: graph.guid(to), instances: new Map() };
          aggregates.set(key, aggregate);
        }
        const fromKey = String(intern(from));
        let targets = aggregate.instances.get(fromKey);
        if (!targets) {
          targets = new Set();
          aggregate.instances.set(fromKey, targets);
        }
        targets.add(intern(to));
      }
      for (const aggregate of aggregates.values()) {
        const instances: Record<string, (number | "global")[]> = {};
        for (const [from, targets] of aggregate.instances) instances[from] = [...targets];
        edges.push({ v: aggregate.v, w: aggregate.w, value: { instances } });
      }
    } else {
      for (const [from, to, sources] of graph.edgeEntries()) {
        if (!(sources & EDGE_FROM_STATE)) continue;
        if (graph.isGlobal(from) && graph.isGlobal(to)) continue;
        edges.push({ v: graph.deref(from), w: graph.deref(to) });
      }
    }
  }

  const result: Record<string, unknown> = {};
  for (const key of ELEMENT_KEYS) {
    if (element[key] !== undefined && element[key] !== null) result[key] = element[key];
  }
  if (graph.options !== undefined) result.options = graph.options;
  // result_payload_graph.rs is_result_graph_value_empty: id and pathingUnhydrated alone don't keep it
  const persistentProperties = graph.persistentProperties;
  if (instancePaths.length > 0 || noEdges || debug || minimal || persistentProperties.length) {
    const value: Record<string, unknown> = {};
    if (element.id !== undefined && element.id !== null) value.id = element.id;
    value.instancePaths = instancePaths;
    if (noEdges) value.noEdges = true;
    if (debug) value.debug = true;
    if (minimal) value.minimal = true;
    if (debugInstancesUnhydrated) value.debugInstancesUnhydrated = true;
    if (pathingUnhydrated) value.pathingUnhydrated = true;
    if (persistentProperties.length) value.persistentProperties = persistentProperties;
    result.value = value;
  }
  result.nodes = nodes;
  result.edges = edges;
  return result;
};

/** The flags decisionapi adds to a `{type: "graph"}` element before building it (was in Turbo.ts). */
export const resolveLegacyGraphElement = (element: LegacyGraphElement): LegacyGraphElement => {
  const { compressEdges: _compressEdges, ...rest } = element;
  return {
    ...rest,
    compressEdges: true,
    minimal: element.minimal ?? false,
    pathingUnhydrated: element.pathingUnhydrated ?? element.minimal !== true,
    debugInstancesUnhydrated: element.debugInstancesUnhydrated ?? (element.debug === true ? true : undefined),
  };
};

/** decisionapi's implicit report graph, which is also what a session saves. */
export const IMPLICIT_REPORT_GRAPH_ELEMENT = { type: "graph", id: "implicit", minimal: true, debug: true };

/**
 * The graph a `{type: "graph", ...element}` request returns, built from a decision graph: the engine's legacy
 * output for the element, then decisionapi's post-processing (expanded edges, hydrated instances, pathing).
 */
export const decisionGraphToGraph = (input: DecisionGraphInput, element: LegacyGraphElement, release?: DecisionGraphRelease) => {
  const raw: any = decisionGraphToLegacyGraph(input, resolveLegacyGraphElement(element), release);
  return rehydrateGraph(raw, {
    derivePathing:
      raw.value?.pathingUnhydrated === true ||
      (raw.value?.id === IMPLICIT_REPORT_GRAPH_ELEMENT.id && raw.value?.minimal === true),
  });
};

/**
 * The legacy graph a session or decision report used to save (the implicit report graph without the pathing
 * fields), for readers of saved graphs. Needs no release: it carries values only, readers merge the rule graph.
 */
export const decisionGraphToStoredGraph = (input: DecisionGraphInput) => {
  const graph = decisionGraphToGraph(input, IMPLICIT_REPORT_GRAPH_ELEMENT);
  for (const node of graph.nodes) {
    delete node.value.path;
    delete node.value.parent_path;
    delete node.value.index;
  }
  return graph;
};
